// src/pages/OnboardSettings.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch, clearStoredCredentials, getAuthHeaders } from '../utils/api';

// ✅ FIXED: No trailing spaces in fallback URL
const API_BASE = (process.env.REACT_APP_API_URL || 'https://localhost:7018/api').trim().replace(/\/$/, '');

function OnboardSettings() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('basic-details');
  const [activeSection, setActiveSection] = useState('business-info');
  const [isEditing, setIsEditing] = useState(false);
  
  // Form Data State (Basic Details) - Cleaned up removed fields
  const [formData, setFormData] = useState({
    businessName: '',
    stateName: '',
    email: '',
    phoneNumber: ''
  });

  // KYC Document State
  const [kycDocuments, setKycDocuments] = useState({
    nin: { file: null, preview: null, uploaded: false, uploading: false, path: null, name: null },
    cac: { file: null, preview: null, uploaded: false, uploading: false, path: null, name: null }
  });

  // Brand Logo State
  const [brandLogo, setBrandLogo] = useState({
    file: null, preview: null, uploaded: false, uploading: false, path: null, name: null
  });

  const getCompanyId = () => {
    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    const id = location.state?.companyId || session?.companyId || session?.CompanyId || location.state?.sessionId || session?.sessionId || null;
    return id ? String(id).trim() : null;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let cleaned = String(phone).replace(/[^\d+]/g, '');
    if (cleaned.startsWith('234')) cleaned = '+' + cleaned;
    if (cleaned.startsWith('0') && cleaned.length === 11) cleaned = '+234' + cleaned.slice(1);
    return cleaned;
  };

  const validateKycFile = (file) => {
    if (!file) return { valid: false, error: 'No file selected' };
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type.toLowerCase())) return { valid: false, error: 'Invalid file type. Allowed: PDF, JPG, PNG' };
    if (file.size > 5 * 1024 * 1024) return { valid: false, error: 'File size must be less than 5MB' };
    return { valid: true, error: null };
  };

  const validateImageFile = (file) => {
    if (!file) return { valid: false, error: 'No file selected' };
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml'];
    if (!validTypes.includes(file.type.toLowerCase())) return { valid: false, error: 'Invalid file type. Allowed: JPG, PNG, GIF, SVG' };
    if (file.size > 2 * 1024 * 1024) return { valid: false, error: 'File size must be less than 2MB' };
    return { valid: true, error: null };
  };

  const uploadKycDocument = async (docType, file, companyId) => {
    const formData = new FormData();
    formData.append('companyId', companyId);
    formData.append('documentType', docType.toLowerCase());
    formData.append('file', file);

    const headers = getAuthHeaders();
    if (!headers) throw new Error('Not authenticated. Please log in again.');

    const result = await authenticatedFetch(`${API_BASE}/HMO/upload-company-document`, 'POST', formData);
    if (result?.status !== 'success' && result?.success !== true) {
      throw new Error(result?.message || 'Upload failed');
    }
    return result;
  };

  const fetchBusinessData = async () => {
    const companyId = getCompanyId();
    if (!companyId) return;

    try {
      const result = await authenticatedFetch(`${API_BASE}/HMO/get-company-by-session`, 'POST', { sessionId: companyId.toString() });

      if (result?.data || result?.status === 'success') {
        const data = result.data || result;
        
        // Cleaned up removed fields
        setFormData({
          businessName: data?.businessName || data?.BusinessName || '',
          stateName: data?.stateName || data?.StateName || data?.stateOfOperation || '',
          email: data?.email || data?.Email || '',
          phoneNumber: formatPhoneNumber(data?.phoneNumber || data?.PhoneNumber || data?.businessPhone || '')
        });

        if (data?.NINPath || data?.ninDocument) {
          const path = data.NINPath || data.ninDocument;
          setKycDocuments(prev => ({ ...prev, nin: { ...prev.nin, preview: path, uploaded: true, path: path, name: path?.split('/').pop() || 'NIN Document' } }));
        }
        
        if (data?.CACPath || data?.cacDocument) {
          const path = data.CACPath || data.cacDocument;
          setKycDocuments(prev => ({ ...prev, cac: { ...prev.cac, preview: path, uploaded: true, path: path, name: path?.split('/').pop() || 'CAC Document' } }));
        }

        if (data?.LogoPath || data?.logoPath || data?.Logo) {
          const path = data.LogoPath || data.logoPath || data.Logo;
          setBrandLogo({ preview: path, uploaded: true, path: path, name: path?.split('/').pop() || 'Company Logo', file: null, uploading: false });
        }
      }
    } catch (err) {
      console.error('Failed to fetch business data:', err);
      toast.error('Could not load business details');
    }
  };

  useEffect(() => {
    const checkAuth = () => {
      const session = localStorage.getItem('userSession');
      if (!session) {
        toast.error('Please log in to continue');
        navigate('/login', { replace: true });
        return false;
      }
      return true;
    };
    
    if (checkAuth()) {
      AOS.init({ duration: 1000, once: true });
      fetchBusinessData();
    }
  }, [navigate]);

  const settingsTabs = [
    { id: 'basic-details', label: 'Basic Details', icon: 'fa-id-card' },
    { id: 'password-security', label: 'Password & Security', icon: 'fa-shield-alt' }
  ];

  const leftMenuItems = [
    { id: 'business-info', label: 'Business Information', icon: 'fa-building' },
    { id: 'kyc-document', label: 'Business KYC/Document', icon: 'fa-file-alt' },
    { id: 'brand-element', label: 'Brand Element (optional)', icon: 'fa-paint-brush' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdate = () => {
    setIsEditing(false);
    toast.success('Settings updated successfully!');
  };

  const handleSelectLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateImageFile(file);
    if (!validation.valid) { toast.error(validation.error); e.target.value = ''; return; }
    setBrandLogo(prev => ({ ...prev, file, preview: URL.createObjectURL(file), uploaded: false, uploading: false }));
    toast.success('Logo file selected. Click "Upload Logo" to submit.');
  };

  const handleUploadLogo = async () => {
    if (!brandLogo?.file) { toast.error('Please select a logo file first'); return; }
    const companyId = getCompanyId();
    if (!companyId) { toast.error('Session expired. Please log in again.'); return; }
    setBrandLogo(prev => ({ ...prev, uploading: true }));
    try {
      const result = await uploadKycDocument('logo', brandLogo.file, companyId);
      if (brandLogo.preview?.startsWith('blob:')) URL.revokeObjectURL(brandLogo.preview);
      setBrandLogo({ file: null, preview: result.path, uploaded: true, uploading: false, path: result.path, name: brandLogo.file.name });
      toast.success('Company logo uploaded successfully!');
      fetchBusinessData();
    } catch (err) {
      setBrandLogo(prev => ({ ...prev, uploading: false }));
      toast.error(`Failed to upload logo: ${err.message}`);
    }
  };

  const handleRemoveLogo = () => {
    if (brandLogo.preview?.startsWith('blob:')) URL.revokeObjectURL(brandLogo.preview);
    setBrandLogo({ file: null, preview: null, uploaded: false, uploading: false, path: null, name: null });
    toast.info('Logo removed');
  };

  const handleSelectNINFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateKycFile(file);
    if (!validation.valid) { toast.error(validation.error); e.target.value = ''; return; }
    const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    setKycDocuments(prev => ({ ...prev, nin: { ...prev.nin, file, preview, uploaded: false, uploading: false } }));
    toast.success('NIN file selected. Click "Upload NIN" to submit.');
  };

  const handleUploadNIN = async () => {
    const doc = kycDocuments.nin;
    if (!doc?.file) { toast.error('Please select a NIN file first'); return; }
    const companyId = getCompanyId();
    if (!companyId) { toast.error('Session expired. Please log in again.'); return; }
    setKycDocuments(prev => ({ ...prev, nin: { ...prev.nin, uploading: true } }));
    try {
      const result = await uploadKycDocument('nin', doc.file, companyId);
      if (doc.preview?.startsWith('blob:')) URL.revokeObjectURL(doc.preview);
      setKycDocuments(prev => ({ ...prev, nin: { file: null, preview: result.path, uploaded: true, uploading: false, path: result.path, name: doc.file.name } }));
      toast.success('NIN document uploaded successfully!');
      fetchBusinessData();
    } catch (err) {
      setKycDocuments(prev => ({ ...prev, nin: { ...prev.nin, uploading: false } }));
      toast.error(`Failed to upload NIN: ${err.message}`);
    }
  };

  const handleRemoveNIN = () => {
    const doc = kycDocuments.nin;
    if (doc.preview?.startsWith('blob:')) URL.revokeObjectURL(doc.preview);
    setKycDocuments(prev => ({ ...prev, nin: { file: null, preview: null, uploaded: false, uploading: false, path: null, name: null } }));
    toast.info('NIN document removed');
  };

  const handleSelectCACFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateKycFile(file);
    if (!validation.valid) { toast.error(validation.error); e.target.value = ''; return; }
    const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    setKycDocuments(prev => ({ ...prev, cac: { ...prev.cac, file, preview, uploaded: false, uploading: false } }));
    toast.success('CAC file selected. Click "Upload CAC" to submit.');
  };

  const handleUploadCAC = async () => {
    const doc = kycDocuments.cac;
    if (!doc?.file) { toast.error('Please select a CAC file first'); return; }
    const companyId = getCompanyId();
    if (!companyId) { toast.error('Session expired. Please log in again.'); return; }
    setKycDocuments(prev => ({ ...prev, cac: { ...prev.cac, uploading: true } }));
    try {
      const result = await uploadKycDocument('cac', doc.file, companyId);
      if (doc.preview?.startsWith('blob:')) URL.revokeObjectURL(doc.preview);
      setKycDocuments(prev => ({ ...prev, cac: { file: null, preview: result.path, uploaded: true, uploading: false, path: result.path, name: doc.file.name } }));
      toast.success('CAC document uploaded successfully!');
      fetchBusinessData();
    } catch (err) {
      setKycDocuments(prev => ({ ...prev, cac: { ...prev.cac, uploading: false } }));
      toast.error(`Failed to upload CAC: ${err.message}`);
    }
  };

  const handleRemoveCAC = () => {
    const doc = kycDocuments.cac;
    if (doc.preview?.startsWith('blob:')) URL.revokeObjectURL(doc.preview);
    setKycDocuments(prev => ({ ...prev, cac: { file: null, preview: null, uploaded: false, uploading: false, path: null, name: null } }));
    toast.info('CAC document removed');
  };

  const handleLogout = (e) => {
    e.preventDefault();
    clearStoredCredentials();
    localStorage.removeItem('userSession');
    localStorage.removeItem('fsl_session');
    localStorage.removeItem('rememberedEmail');
    toast.success('Logged out');
    navigate('/login', { replace: true });
  };

  const getSectionTitle = () => {
    const section = leftMenuItems.find(item => item.id === activeSection);
    return section ? section.label : '';
  };

  const renderBasicDetailsContent = () => {
    switch (activeSection) {
      case 'business-info':
        return (
          <div className="settings-form" data-aos="fade-up">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Business name</label>
                <input type="text" name="businessName" className="form-control" value={formData.businessName} onChange={handleInputChange} disabled={!isEditing} placeholder="Enter business name" />
              </div>
              <div className="form-group">
                <label className="form-label">State Name</label>
                <input type="text" name="stateName" className="form-control" value={formData.stateName} onChange={handleInputChange} disabled={!isEditing} placeholder="e.g., Lagos, Anambra, Abuja" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input type="email" className="form-control" value={formData.email} disabled placeholder="Email from registration" />
                <small className="text-muted">Email is managed during registration</small>
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input type="tel" className="form-control" value={formData.phoneNumber} disabled placeholder="+2347068420039" />
                <small className="text-muted">Phone is managed during registration</small>
              </div>
            </div>
            <div className="info-box">
              <i className="fas fa-info-circle"></i>
              <p>Note: Email and Phone Number are set during registration. Contact support to update them.</p>
            </div>
            {isEditing && (
              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleUpdate}>Update</button>
                <button className="btn btn-secondary" onClick={() => setIsEditing(false)}>Cancel</button>
              </div>
            )}
          </div>
        );

      case 'kyc-document':
        return (
          <div className="settings-section">
            <p className="text-muted mb-4">Upload your mandatory KYC documents for verification.</p>
            
            {/* NIN Document Upload Card */}
            <div className="document-upload-card mb-4">
              <div className="document-header">
                <div className="d-flex align-items-center gap-2">
                  <i className="fas fa-id-card text-orange"></i>
                  <h6 className="mb-0">NIN Document</h6>
                </div>
                {kycDocuments.nin.uploaded && <span className="badge bg-success">✓ Uploaded</span>}
              </div>
              <p className="text-muted small mb-3">Upload a clear copy of your National Identity Number (NIN) slip or card.</p>
              {!kycDocuments.nin.uploaded ? (
                <div className="upload-area">
                  {kycDocuments.nin.preview ? (
                    <div className="preview-container">
                      {kycDocuments.nin.preview.startsWith('blob:') ? (
                        <img src={kycDocuments.nin.preview} alt="NIN Preview" className="document-preview" />
                      ) : (
                        <div className="file-preview">
                          <i className="fas fa-file-pdf fa-3x text-danger"></i>
                          <p className="mt-2 mb-0 small">{kycDocuments.nin.file?.name || kycDocuments.nin.name}</p>
                        </div>
                      )}
                      <div className="d-flex gap-2 justify-content-center mt-2">
                        <button className="btn btn-sm btn-danger" onClick={handleRemoveNIN}><i className="fas fa-trash me-1"></i> Remove</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt"></i>
                      <h6>Drag & drop or click to browse</h6>
                      <p className="text-muted small">PDF, JPG, PNG • Max 5MB</p>
                      <input type="file" id="nin-upload" className="d-none" accept=".pdf,.jpg,.jpeg,.png" onChange={handleSelectNINFile} />
                      <label htmlFor="nin-upload" className="btn btn-secondary mt-2" style={{ cursor: 'pointer' }}><i className="fas fa-folder-open me-1"></i> Select NIN File</label>
                    </>
                  )}
                  {kycDocuments.nin.file && !kycDocuments.nin.uploaded && !kycDocuments.nin.uploading && (
                    <button className="btn btn-primary mt-3" style={{ backgroundColor: '#F89423', borderColor: '#F89423' }} onClick={handleUploadNIN}><i className="fas fa-upload me-1"></i> Upload NIN</button>
                  )}
                  {kycDocuments.nin.uploading && (
                    <button className="btn btn-primary mt-3" disabled style={{ backgroundColor: '#F89423', borderColor: '#F89423' }}>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span> Uploading...
                    </button>
                  )}
                </div>
              ) : (
                <div className="uploaded-state text-center py-3">
                  <i className="fas fa-check-circle text-success fa-2x mb-2"></i>
                  <p className="mb-0 text-muted small">Document uploaded successfully</p>
                  {kycDocuments.nin.name && <small className="d-block text-muted mt-1">{kycDocuments.nin.name}</small>}
                  <button className="btn btn-sm btn-outline-secondary mt-2" onClick={handleRemoveNIN}><i className="fas fa-redo me-1"></i> Replace Document</button>
                </div>
              )}
            </div>

            {/* CAC Document Upload Card */}
            <div className="document-upload-card">
              <div className="document-header">
                <div className="d-flex align-items-center gap-2">
                  <i className="fas fa-file-contract text-orange"></i>
                  <h6 className="mb-0">CAC Document</h6>
                </div>
                {kycDocuments.cac.uploaded && <span className="badge bg-success">✓ Uploaded</span>}
              </div>
              <p className="text-muted small mb-3">Upload your Corporate Affairs Commission (CAC) registration certificate.</p>
              {!kycDocuments.cac.uploaded ? (
                <div className="upload-area">
                  {kycDocuments.cac.preview ? (
                    <div className="preview-container">
                      {kycDocuments.cac.preview.startsWith('blob:') ? (
                        <img src={kycDocuments.cac.preview} alt="CAC Preview" className="document-preview" />
                      ) : (
                        <div className="file-preview">
                          <i className="fas fa-file-pdf fa-3x text-danger"></i>
                          <p className="mt-2 mb-0 small">{kycDocuments.cac.file?.name || kycDocuments.cac.name}</p>
                        </div>
                      )}
                      <div className="d-flex gap-2 justify-content-center mt-2">
                        <button className="btn btn-sm btn-danger" onClick={handleRemoveCAC}><i className="fas fa-trash me-1"></i> Remove</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt"></i>
                      <h6>Drag & drop or click to browse</h6>
                      <p className="text-muted small">PDF, JPG, PNG • Max 5MB</p>
                      <input type="file" id="cac-upload" className="d-none" accept=".pdf,.jpg,.jpeg,.png" onChange={handleSelectCACFile} />
                      <label htmlFor="cac-upload" className="btn btn-secondary mt-2" style={{ cursor: 'pointer' }}><i className="fas fa-folder-open me-1"></i> Select CAC File</label>
                    </>
                  )}
                  {kycDocuments.cac.file && !kycDocuments.cac.uploaded && !kycDocuments.cac.uploading && (
                    <button className="btn btn-primary mt-3" style={{ backgroundColor: '#F89423', borderColor: '#F89423' }} onClick={handleUploadCAC}><i className="fas fa-upload me-1"></i> Upload CAC</button>
                  )}
                  {kycDocuments.cac.uploading && (
                    <button className="btn btn-primary mt-3" disabled style={{ backgroundColor: '#F89423', borderColor: '#F89423' }}>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span> Uploading...
                    </button>
                  )}
                </div>
              ) : (
                <div className="uploaded-state text-center py-3">
                  <i className="fas fa-check-circle text-success fa-2x mb-2"></i>
                  <p className="mb-0 text-muted small">Document uploaded successfully</p>
                  {kycDocuments.cac.name && <small className="d-block text-muted mt-1">{kycDocuments.cac.name}</small>}
                  <button className="btn btn-sm btn-outline-secondary mt-2" onClick={handleRemoveCAC}><i className="fas fa-redo me-1"></i> Replace Document</button>
                </div>
              )}
            </div>

            <div className="info-box mt-4">
              <i className="fas fa-shield-alt"></i>
              <p className="mb-0">Your documents are encrypted and stored securely.</p>
            </div>
          </div>
        );

      case 'brand-element':
        return (
          <div className="settings-section">
            <p className="text-muted mb-4">Customize your brand elements like logo and colors.</p>
            <div className="document-upload-card">
              <div className="document-header">
                <div className="d-flex align-items-center gap-2">
                  <i className="fas fa-image text-orange"></i>
                  <h6 className="mb-0">Company Logo</h6>
                </div>
                {brandLogo.uploaded && <span className="badge bg-success">✓ Uploaded</span>}
              </div>
              <p className="text-muted small mb-3">Upload your company logo. Recommended: PNG or SVG, max 2MB.</p>
              {!brandLogo.uploaded ? (
                <div className="upload-area">
                  {brandLogo.preview ? (
                    <div className="preview-container">
                      {brandLogo.preview.startsWith('blob:') ? (
                        <img src={brandLogo.preview} alt="Logo Preview" className="document-preview" style={{ maxWidth: '200px' }} />
                      ) : (
                        <img src={`${API_BASE}${brandLogo.preview}`} alt="Company Logo" className="document-preview" style={{ maxWidth: '200px' }} />
                      )}
                      <div className="d-flex gap-2 justify-content-center mt-2">
                        <button className="btn btn-sm btn-danger" onClick={handleRemoveLogo}><i className="fas fa-trash me-1"></i> Remove</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt"></i>
                      <h6>Drag & drop or click to browse</h6>
                      <p className="text-muted small">JPG, PNG, GIF, SVG • Max 2MB</p>
                      <input type="file" id="logo-upload" className="d-none" accept="image/jpeg,image/png,image/gif,image/svg+xml" onChange={handleSelectLogoFile} />
                      <label htmlFor="logo-upload" className="btn btn-secondary mt-2" style={{ cursor: 'pointer' }}><i className="fas fa-folder-open me-1"></i> Select Logo File</label>
                    </>
                  )}
                  {brandLogo.file && !brandLogo.uploaded && !brandLogo.uploading && (
                    <button className="btn btn-primary mt-3" style={{ backgroundColor: '#F89423', borderColor: '#F89423' }} onClick={handleUploadLogo}><i className="fas fa-upload me-1"></i> Upload Logo</button>
                  )}
                  {brandLogo.uploading && (
                    <button className="btn btn-primary mt-3" disabled style={{ backgroundColor: '#F89423', borderColor: '#F89423' }}>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span> Uploading...
                    </button>
                  )}
                </div>
              ) : (
                <div className="uploaded-state text-center py-3">
                  <i className="fas fa-check-circle text-success fa-2x mb-2"></i>
                  <p className="mb-0 text-muted small">Logo uploaded successfully</p>
                  {brandLogo.name && <small className="d-block text-muted mt-1">{brandLogo.name}</small>}
                  {brandLogo.path && <img src={`${API_BASE}${brandLogo.path}`} alt="Company Logo" className="mt-2" style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain' }} />}
                  <button className="btn btn-sm btn-outline-secondary mt-2" onClick={handleRemoveLogo}><i className="fas fa-redo me-1"></i> Replace Logo</button>
                </div>
              )}
            </div>
            <div className="info-box mt-4">
              <i className="fas fa-lightbulb"></i>
              <p className="mb-0">Your logo will appear on invoices, reports, and customer-facing documents.</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic-details':
        return (
          <div className="settings-content">
            <div className="settings-layout">
              <div className="settings-sidebar">
                {leftMenuItems.map(item => (
                  <button key={item.id} className={`settings-menu-item ${activeSection === item.id ? 'active' : ''}`} onClick={() => setActiveSection(item.id)}>
                    <span>{item.label}</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                ))}
              </div>
              <div className="settings-main">
                <div className="section-header">
                  <h4>{getSectionTitle()}</h4>
                  {!isEditing && activeSection === 'business-info' && (
                    <button className="btn-edit" onClick={() => setIsEditing(true)}><i className="fas fa-edit"></i> Edit</button>
                  )}
                  {isEditing && (
                    <button className="btn-edit cancel" onClick={() => setIsEditing(false)}><i className="fas fa-times"></i> Cancel</button>
                  )}
                </div>
                {renderBasicDetailsContent()}
              </div>
            </div>
          </div>
        );
      case 'password-security':
        return (
          <div className="settings-content">
            <div className="card">
              <div className="card-body p-4">
                <h5 className="mb-4">Password & Security</h5>
                <div className="form-group">
                  <label className="form-label">Current Password</label>
                  <input type="password" className="form-control" />
                </div>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input type="password" className="form-control" />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input type="password" className="form-control" />
                </div>
                <button className="btn btn-primary" style={{ backgroundColor: '#F89423', borderColor: '#F89423' }}>Update Password</button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="settings-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      {/* Sidebar - Matches Onboarding Dashboard */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <div className="brand-logo">
            <img src="images/fsl_logo.png" alt="FSL Logo" className="fsl-logo" />
          </div>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-section">
            <button className="nav-link text-white border-0 bg-transparent w-100 text-start" onClick={() => navigate('/onboarding-dashboard')} style={{ cursor: 'pointer' }}>
              <i className="fas fa-home"></i><span>Get Started</span>
            </button>
          </div>
          <div className="nav-section">
            <div className="nav-section-title">DASHBOARD</div>
            <ul className="nav-list">
              <li><div className="menu-placeholder"></div></li>
              <li><div className="menu-placeholder"></div></li>
            </ul>
          </div>
          <div className="nav-section">
            <div className="nav-section-title">ADMIN</div>
            <ul className="nav-list">
              <li><div className="menu-placeholder"></div></li>
              <li><div className="menu-placeholder"></div></li>
            </ul>
          </div>
          <div className="nav-section">
            <div className="nav-section-title">PRODUCTS</div>
            <ul className="nav-list">
              <li><div className="menu-placeholder"></div></li>
              <li><div className="menu-placeholder"></div></li>
            </ul>
          </div>
          <div className="nav-section">
            <div className="nav-section-title">TRANSACTIONS</div>
            <ul className="nav-list">
              <li><div className="menu-placeholder"></div></li>
              <li><div className="menu-placeholder"></div></li>
            </ul>
          </div>
          <div className="nav-section">
            <div className="nav-section-title">ACCOUNT</div>
            <ul className="nav-list">
              <li>
                <button className="nav-link active text-white border-0 bg-transparent w-100 text-start" style={{ cursor: 'pointer' }}>
                  <i className="fas fa-cog"></i><span>Settings</span>
                </button>
              </li>
              <li>
                <button className="nav-link text-white border-0 bg-transparent w-100 text-start" onClick={handleLogout} style={{ cursor: 'pointer' }}>
                  <i className="fas fa-sign-out-alt"></i><span>Logout</span>
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button className="menu-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle sidebar"><i className="fas fa-bars"></i></button>
            <h1 className="page-title"><i className="fas fa-arrow-left me-2" style={{ cursor: 'pointer' }} onClick={() => navigate('/onboarding-dashboard')}></i>Settings</h1>
          </div>
          <div className="header-right">
            <button className="header-icon" onClick={() => navigate('/onboarding-dashboard')} title="Go to Dashboard" style={{ cursor: 'pointer' }}>
              <i className="fas fa-th-list"></i>
            </button>
            <div className="user-avatar" title="User">U</div>
          </div>
        </header>

        <div className="content-wrapper">
          <div className="settings-tabs">
            {settingsTabs.map(tab => (
              <button key={tab.id} className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
                <i className={`fas ${tab.icon}`}></i>
                {tab.label}
              </button>
            ))}
          </div>
          {renderTabContent()}
        </div>
      </div>

      {/* CSS - Matches Onboarding Dashboard Theme */}
      <style>{`
        :root { --primary-orange: #F89423; --dark-orange: #ea7c1f; --light-orange: #f2a128; --sidebar-bg: #f2a128; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: var(--primary-orange); border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: var(--dark-orange); }
        
        .settings-page { display: flex; min-height: 100vh; background-color: #fafafa; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        
        .sidebar { width: 260px; background: var(--sidebar-bg); color: white; position: fixed; left: 0; top: 0; height: 100vh; overflow-y: auto; transition: transform 0.3s ease; z-index: 1000; }
        .sidebar-header { padding: 24px 20px; border-bottom: 1px solid rgba(255,255,255,0.1); background: white; }
        .brand-logo { display: flex; align-items: center; gap: 12px; color: #333; font-size: 20px; font-weight: 700; }
        .fsl-logo { height: 40px; width: auto; }
        .sidebar-nav { padding: 20px 0; }
        .nav-section { margin-bottom: 24px; }
        .nav-section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: rgba(255,255,255,0.7); padding: 0 20px; margin-bottom: 12px; }
        .nav-list { list-style: none; padding: 0; margin: 0; }
        .nav-link { display: flex; align-items: center; padding: 12px 20px; color: rgba(255,255,255,0.9); text-decoration: none; transition: all 0.3s ease; font-weight: 500; font-size: 14px; border-left: 3px solid transparent; }
        .nav-link i { width: 20px; margin-right: 12px; font-size: 16px; }
        .nav-link:hover { background-color: rgba(255,255,255,0.1); border-left-color: white; color: white; }
        .nav-link.active { background-color: rgba(255,255,255,0.15); border-left-color: white; color: white; font-weight: 600; }
        .menu-placeholder { height: 40px; background: rgba(255,255,255,0.15); border-radius: 6px; margin: 0 20px 8px 20px; }
        
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; background-color: #fafafa; }
        .top-header { background: white; padding: 16px 30px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100; }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .menu-toggle { background: none; border: none; font-size: 20px; color: #374151; cursor: pointer; display: none; }
        .page-title { display: flex; align-items: center; color: #1f2937; font-size: 20px; font-weight: 600; margin: 0; }
        .header-right { display: flex; align-items: center; gap: 12px; }
        .header-icon { width: 40px; height: 40px; border-radius: 50%; border: none; background: #f3f4f6; color: #6b7280; cursor: pointer; position: relative; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; font-size: 16px; }
        .header-icon:hover { background: #e5e7eb; color: var(--primary-orange); }
        .user-avatar { width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, var(--primary-orange) 0%, var(--dark-orange) 100%); color: white; display: flex; align-items: center; justify-content: center; font-weight: 600; cursor: pointer; font-size: 14px; }
        
        .content-wrapper { padding: 30px; flex: 1; }
        
        .settings-tabs { display: flex; gap: 10px; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #e5e7eb; }
        .settings-tab { display: flex; align-items: center; gap: 8px; padding: 10px 20px; border: none; background: #f3f4f6; border-radius: 8px; cursor: pointer; font-weight: 500; color: #6b7280; transition: all 0.2s ease; }
        .settings-tab:hover { background: #e5e7eb; }
        .settings-tab.active { background: var(--primary-orange); color: white; }
        
        .settings-layout { display: flex; gap: 24px; }
        .settings-sidebar { width: 280px; background: white; border-radius: 12px; border: 1px solid #e5e7eb; padding: 16px; height: fit-content; }
        .settings-menu-item { display: flex; justify-content: space-between; align-items: center; width: 100%; padding: 12px 16px; border: none; background: none; text-align: left; cursor: pointer; border-radius: 8px; color: #374151; font-weight: 500; transition: all 0.2s ease; }
        .settings-menu-item:hover { background: #f9fafb; }
        .settings-menu-item.active { background: #fef3e2; color: var(--primary-orange); font-weight: 600; }
        .settings-main { flex: 1; background: white; border-radius: 12px; border: 1px solid #e5e7eb; padding: 24px; }
        
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #e5e7eb; }
        .btn-edit { display: flex; align-items: center; gap: 6px; padding: 8px 16px; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 8px; color: #374151; cursor: pointer; font-weight: 500; transition: all 0.2s ease; }
        .btn-edit:hover { background: #e5e7eb; }
        .btn-edit.cancel { background: #fee2e2; border-color: #fecaca; color: #ef4444; }
        
        .form-label { display: block; margin-bottom: 6px; font-weight: 600; color: #374151; font-size: 14px; }
        .form-control, .form-select { padding: 10px 14px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; width: 100%; transition: all 0.2s ease; }
        .form-control:focus, .form-select:focus { border-color: var(--primary-orange); box-shadow: 0 0 0 3px rgba(248, 148, 35, 0.15); outline: none; }
        .form-control:disabled, .form-select:disabled { background-color: #f3f4f6; cursor: not-allowed; opacity: 0.8; }
        .form-control.textarea { min-height: 100px; resize: vertical; }
        .form-row { display: flex; gap: 20px; margin-bottom: 20px; }
        .form-group { flex: 1; margin-bottom: 20px; }
        
        .info-box { display: flex; align-items: flex-start; gap: 12px; padding: 16px; background: #fef3e2; border-radius: 8px; margin: 20px 0; }
        .info-box i { color: var(--primary-orange); margin-top: 2px; }
        .info-box p { margin: 0; color: #92400e; font-size: 14px; }
        
        .form-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px; padding-top: 24px; border-top: 1px solid #e5e7eb; }
        
        .upload-area { border: 2px dashed #d1d5db; border-radius: 12px; padding: 30px; text-align: center; background: #f9fafb; transition: all 0.2s ease; }
        .upload-area:hover { border-color: var(--primary-orange); background: #fffbf5; }
        .upload-area i { font-size: 48px; color: #9ca3af; margin-bottom: 16px; }
        .upload-area h6 { margin: 0 0 8px; color: #374151; }
        .upload-area p { margin: 0; color: #6b7280; font-size: 13px; }
        
        .document-upload-card { background: white; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; }
        .document-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #f3f4f6; }
        .document-header h6 { color: #1f2937; font-weight: 600; }
        .preview-container { text-align: center; }
        .document-preview { max-width: 100%; max-height: 200px; border-radius: 8px; margin-bottom: 12px; border: 1px solid #e5e7eb; object-fit: contain; }
        .file-preview { padding: 20px; background: #f9fafb; border-radius: 8px; margin-bottom: 12px; display: flex; flex-direction: column; align-items: center; }
        .uploaded-state { background: #f0fdf4; border: 1px solid #86efac; border-radius: 8px; }
        
        .card { border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); background: white; border: none; }
        .text-orange { color: var(--primary-orange); }
        
        .btn { padding: 10px 20px; border-radius: 8px; font-weight: 500; cursor: pointer; transition: all 0.2s ease; border: none; font-size: 14px; }
        .btn-primary { background: var(--primary-orange); color: white; }
        .btn-primary:hover { background: var(--dark-orange); }
        .btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #d1d5db; }
        .btn-outline-secondary { background: white; color: #6b7280; border: 1px solid #d1d5db; }
        .btn-sm { padding: 6px 12px; font-size: 13px; }
        .btn-danger { background: #fee2e2; color: #ef4444; border: 1px solid #fecaca; }
        
        @media (max-width: 992px) { .sidebar { transform: translateX(-100%); } .sidebar.sidebar-open { transform: translateX(0); } .main-content { margin-left: 0; } .menu-toggle { display: block; } .settings-layout { flex-direction: column; } .settings-sidebar { width: 100%; } }
        @media (max-width: 768px) { .content-wrapper { padding: 16px; } .top-header { padding: 16px; } .form-row { flex-direction: column; gap: 0; } }
      `}</style>
    </div>
  );
}

export default OnboardSettings;